import 'regenerator-runtime/runtime';
import { call, cancel, select, takeEvery } from 'redux-saga/effects';

import {
  getAuslandsRisikoZuschlag,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorschlagsdatenRequestPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  Fachbaustein,
  Feature,
  SpartenType,
  ThhPferdDatenAntrag,
  VorschadenArt,
} from '@ps20/bff-models';
import {
  App,
  convertToBffLaufzeit,
  DIPLOMATEN_CODE,
  FachschluesselNumber,
  getFachschluessel,
  handleLinieFromVorvertrag,
  mapToBffVorvertragsNummer,
  Produktlinie,
} from '@ps20-frontend/frontend-models';
import dayjs from 'dayjs';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { selectSelectedProduktlinie } from '../../app/shared/selectors/produkt.selector';
import { IAppState } from '../appState';

export function* watchVorschlagdatenChanged() {
  yield takeEvery(
    GlobalActions.VORSCHLAGDATEN_REQUESTED,
    safeSaga(vorschlagdatenSaga, App.THH_PFERD),
  );
}

export function* vorschlagdatenSaga(action: GlobalAction<VorschlagsdatenRequestPayload>) {
  const angebote = action.payload.angebotsIds;

  if (!angebote.some(angebot => angebot.appId === App.THH_PFERD)) {
    yield cancel();
    return;
  }

  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();

  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);

  const vorschlagdaten: ThhPferdDatenAntrag = {
    spartenType: SpartenType.TIERHALTERHAFTPFLICHT_PFERD,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    jahresbeitrag: state.jahresbeitrag,
    savedJahresbeitragMitRabatt: state.savedJahresbeitragMitRabatt,
    gesamtpreisNachZahlweise: mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
      state.vertragsoptionen.zahlungsweise,
      state.gesamtbeitragMitRabatt,
      state.jahresbeitrag,
      App.THH_PFERD,
    ),
    savedGesamtbeitragMitRabatt: state.savedGesamtbeitragMitRabatt,
    antragsId: state.antragsnummer,
    sicherungsglaeubiger: null,
    hspLfnr: state.produkt.hspLfnr,
    vorschaedenVorhanden: state.vorschaeden.vorhanden,
    vorschaeden: [
      {
        vorschadenArt: VorschadenArt.THH_PFERD,
        anzahl: state.vorschaeden.thhPferdVorschaeden,
        betrachtungszeitraum: 5,
      },
    ],
    tierAntrag: [],
    auslandsRisikoZuschlag: getAuslandsRisikoZuschlag(
      state.vertragsoptionen?.selectedKooperation === DIPLOMATEN_CODE,
      ausgewaehlteProduktlinie.nachlass,
    ),
    produkt: {
      beginn: null,
      ablauf: null,
      selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
      produktline: ausgewaehlteProduktlinie.produktName,
      kurztext: ausgewaehlteProduktlinie.kurztext,
      langtext: ausgewaehlteProduktlinie.langtext,
      fachschluessel: setFachschluessel(state, ausgewaehlteProduktlinie),
    },
    rbdMax: ausgewaehlteProduktlinie.rbdMax,
    rbdAktuell: state.taasInfo.rbdNachlass,
    treuebonus: state.taasInfo.tbNachlass,
    savedRbdNachlass: state.taasInfo.savedRbdNachlass,
    savedTbNachlass: state.taasInfo.savedTbNachlass,
    vorteilsrabatt: state.taasInfo.vorteilsrabatt,
    preisverzeichnis: ausgewaehlteProduktlinie.preisverzeichnis,
    vorversicherung: [],
    antragSpeichernDatum: dayjs().format(DATEFORMAT_SHORT_ISO),
    laufzeit: convertToBffLaufzeit(state.vertragsoptionen.vertragslaufzeit),
    rabattstufe: state.rabattstufe,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
    vorvertragsnummern: mapToBffVorvertragsNummer(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    zielmarktAbweichung: false,
    kooperation: state.vertragsoptionen.selectedKooperation,
  };

  handleLinieFromVorvertrag(vorschlagdaten, ausgewaehlteProduktlinie, state.vertragsoptionen);

  yield call(globalPut.put, new GlobalActions().vorschlagsdatenGenerated(vorschlagdaten));
}

export function setFachschluessel(
  state: IAppState,
  selectedProduktlinie: Produktlinie,
): Array<Fachbaustein> {
  const fachschluessel = getFachschluessel(state.produkt);
  fachschluessel.forEach(fschl => {
    if (
      state.vertragsoptionen?.beibehalten &&
      fschl.fachschluessel === FachschluesselNumber.PFERDEHALTERHAFTPFLICHT
    ) {
      selectedProduktlinie.gefahren
        .filter(gefahr => gefahr.name === 'P')
        .forEach(hauptGefahr => {
          fschl.deltafaktor = hauptGefahr.deltafaktor;
          fschl.anzahlVorschaeden = hauptGefahr.anzahlVorschaeden;
          fschl.plzFaktor = hauptGefahr.plzFaktor;
        });
    }
  });

  return fachschluessel;
}
